import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";

const LINKS = [
  { to: "/product", label: "Product" },
  { to: "/pricing", label: "Pricing" },
  { to: "/industries", label: "Industries" },
  { to: "/boat-club", label: "Boat Clubs" },
  { to: "/resources", label: "Resources" },
  { to: "/about", label: "About" },
];

const OPERATOR_LOGIN_URL = "https://operator.rentanything.ai/login";

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-all ${
        scrolled
          ? "border-ra-line bg-white/80 backdrop-blur-md"
          : "border-transparent bg-white/0"
      }`}
      data-testid="site-nav"
    >
      <div className="ra-container flex h-16 items-center justify-between">
        <Logo />

        <nav className="hidden md:flex items-center gap-1" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}-link`}
              className={({ isActive }) =>
                `relative px-3 py-2 text-sm font-medium transition-colors ${
                  isActive ? "text-ra-ocean" : "text-ra-ink/75 hover:text-ra-ink"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <span className="absolute left-3 right-3 -bottom-[1px] h-[2px] rounded-full bg-ra-ocean" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <a
            href={OPERATOR_LOGIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="ra-btn-ghost"
            data-testid="nav-operator-login-btn"
          >
            Operator Login
          </a>
          <Link to="/contact" className="ra-btn-primary" data-testid="nav-book-demo-btn">
            Book a Demo
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex items-center justify-center rounded-full border border-ra-line bg-white p-2 text-ra-ink"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-ra-line bg-white" data-testid="nav-mobile-panel">
          <div className="ra-container flex flex-col py-4">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="py-3 text-base font-medium text-ra-ink"
                data-testid={`nav-mobile-${l.label.toLowerCase()}-link`}
              >
                {l.label}
              </Link>
            ))}
            <div className="mt-2 grid grid-cols-2 gap-2">
              <a
                href={OPERATOR_LOGIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="ra-btn-ghost"
                data-testid="nav-mobile-operator-login-btn"
              >
                Operator Login
              </a>
              <Link to="/contact" className="ra-btn-primary" data-testid="nav-mobile-book-demo-btn">
                Book a Demo
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
